/* eslint-disable camelcase */

import dayjs from 'dayjs'
import _ from 'lodash'
import { isBrowser, isTablet } from 'react-device-detect'
import { createSelector } from 'reselect'
import {
  getAdls,
  getCarePlanHistory,
  getCarePlanList,
  getUsersList
} from './Lists'
import { populateLogs } from './Logs'
import { getRiskAssessments } from './RiskAssessments'

const isDesktop = isBrowser || isTablet

const getCarePlanSortType = (state) => state.carePlans.carePlanSortType

export const sortAdls = createSelector(
  [getAdls, getCarePlanSortType],
  (adls, sortType) => {
    if (!adls) return null
    const withAlerts = adls.map(getAlertsForItem)
    switch (sortType) {
      case 'a-z':
        return _.sortBy(withAlerts, 'name')
      case 'last-review-date': {
        const withoutReviewDate = _.filter(withAlerts, adlHasNotBeenReviewed)
        return _(withAlerts)
          .reject(adlHasNotBeenReviewed)
          .sortBy('carePlan.editedOn')
          .reverse()
          .concat(withoutReviewDate)
          .value()
      }
      case 'next-review-date':
        return _.sortBy(withAlerts, 'carePlan.nextReviewDate')
      default:
        return withAlerts
    }
  }
)

export const groupCarePlansHistoryByDate = createSelector(
  [getCarePlanHistory],
  (history) => {
    if (!history) return null
    return _(history)
      .sortBy(chooseCarePlanSortDate)
      .reverse()
      .groupBy((carePlan) => {
        const date = chooseCarePlanSortDate(carePlan)
        return dayjs(date).startOf('month').format('MMMM YYYY')
      })
      .value()
  }
)

/**
 *
 * @param {*} _state
 * @param {*} props
 * @returns
 */
const getCarePlanId = (_state, props) => {
  if (!props.router.params) return null
  const {
    router: {
      params: { care_plan_id, revision_id }
    }
  } = props
  return revision_id || care_plan_id
}

export const getCarePlan = createSelector(
  [getCarePlanId, getCarePlanList, getUsersList, getRiskAssessments],
  (carePlanId, carePlans, carers, allRiskAssessments) => {
    const carePlan = _.find(carePlans, { id: carePlanId })
    if (!carePlans || !carePlan || !carers || !allRiskAssessments) return null

    const carersById = _.keyBy(carers, 'id')
    const assessmentsById = _.keyBy(allRiskAssessments, 'id')
    const { editedBy, createdBy, riskAssessments } = carePlan
    const carePlanAssessments =
      riskAssessments && riskAssessments?.map((id) => assessmentsById[id])

    const seenBy = carePlan.seenBy
      ?.map((id) => _.find(carers, { id }))
      .filter((d) => d) // filter out undefined

    return {
      ...carePlan,
      editedBy: editedBy && carersById[editedBy],
      createdBy: createdBy && carersById[createdBy],
      riskAssessments: _.every(carePlanAssessments, isTruthy)
        ? carePlanAssessments
        : [],
      seenBy
    }
  }
)

/**
 * Get all logs beloning to the current care plan
 * @param {*} state: any
 * @param {*} props: any
 */
export const getCarePlanLogs = createSelector(
  [populateLogs, getCarePlan],
  (logs, carePlan) => {
    if (!carePlan || !logs) return null
    return _.filter(logs, { carePlanId: carePlan.carePlanId })
  }
)

export const getCarePlanReportData = (state) =>
  state.data['care-plans/dashboard']
export const getCarePlansReport = createSelector(
  [getCarePlanReportData],
  (carePlans) => {
    if (!carePlans) return null
    const withAlerts = _(carePlans)
      .map(getAlertsForItem)
      .filter((carePlan) => !!carePlan.nextReviewDate)

    return {
      today: filterByNextReviewDate(withAlerts, (date) =>
        date.isSame(dayjs(), 'd')
      ),
      tomorrow: filterByNextReviewDate(withAlerts, (date) => {
        const tomorrow = dayjs().add(1, 'days').startOf('day')
        return date.isSame(tomorrow, 'd')
      }),
      thisWeek: filterByNextReviewDate(
        withAlerts,
        (date) =>
          dayjs().add(2, 'days').startOf('day') <= date &&
          dayjs().add(7, 'days').endOf('day') >= date
      ),
      thisMonth: filterByNextReviewDate(
        withAlerts,
        (date) =>
          dayjs().add(8, 'days').startOf('day') <= date &&
          dayjs().add(1, 'months').endOf('day') >= date
      ),
      overdue: filterByNextReviewDate(
        withAlerts,
        (date) => dayjs().subtract(1, 'days').endOf('day') >= date
      )
    }
  }
)

export const getCarePlansReportByResident = createSelector(
  [getCarePlansReport],
  (report) => {
    if (!report) return null

    const result = {}

    for (const group in report) {
      const carePlans = report[group]
      result[group] = _(carePlans)
        .sortBy((row) => row.customName || row.carePlanAdl.name)
        .groupBy('resident.id')
        .value()
    }
    return result
  }
)

const getAlertsForItem = (item) => {
  // show alert in dashboard if risk assessment updated or incomplete

  if (
    !item.riskAssessments &&
    (!item.carePlan || !item.carePlan.riskAssessments)
  ) {
    return item
  }

  const carePlan = item.carePlan || item
  const updatedAssessments = getUpdatedAssessmentsForCarePlan(carePlan)

  if (updatedAssessments.length) {
    const isOverdue = dayjs(carePlan.nextReviewDate).isBefore(dayjs())
    const alert = {
      text: getUpdatedAssessmentAlert(updatedAssessments[0]),
      type: 'updated'
    }
    const updatedCarePlan = {
      ...carePlan,
      nextReviewDate: isOverdue
        ? carePlan.nextReviewDate
        : dayjs().toISOString()
    }

    if (item.carePlan) {
      return {
        ...item,
        carePlan: updatedCarePlan,
        alert
      }
    }
    return {
      ...updatedCarePlan,
      alert
    }
  }

  const incompleteAssessments = getIncompleteAssessmentsForCarePlan(carePlan)
  if (incompleteAssessments.length) {
    return {
      ...item,
      alert: {
        text: getIncompleteAssessmentAlert(incompleteAssessments[0]),
        type: 'incomplete'
      }
    }
  }

  return item
}

const adlHasNotBeenReviewed = (adl) => !adl.carePlan || !adl.carePlan.editedOn

const getUpdatedAssessmentsForCarePlan = (carePlan) =>
  _(carePlan.riskAssessments)
    .filter(
      (assessment) =>
        assessment &&
        assessment.createdOn &&
        dayjs(assessment.createdOn).isAfter(dayjs(carePlan.editedOn))
    )
    .sortBy('createdOn')
    .value()

const getIncompleteAssessmentsForCarePlan = (carePlan) =>
  _(carePlan.riskAssessments)
    .filter((item) => item && item.incomplete)
    .sortBy('createdOn')
    .value()

const getUpdatedAssessmentAlert = (assessment) => {
  const name = assessment.form?.name || 'Risk Assessment'
  const date = dayjs(assessment.createdOn).format('DD-MM-YYYY')

  return `${name} was updated on ${date}`
}

const getIncompleteAssessmentAlert = (assessment) => {
  const name = assessment.form?.name || 'Risk Assessment'

  if (isDesktop) {
    return `${name} is incomplete`
  }
  return 'Assessment incomplete'
}

const chooseCarePlanSortDate = (cp) => cp.editedOn || cp.createdOn

const isTruthy = (assessment) => !!assessment

const filterByNextReviewDate = (carePlans, filterFunc) =>
  _(carePlans)
    .filter((carePlan) => {
      const date = dayjs(carePlan.nextReviewDate).startOf('day')
      return filterFunc(date)
    })
    .sortBy('nextReviewDate')
    .value()
